// Einstellungen → Kalender → Termin-Menü: Jeder Mandant stellt sich das
// Rechtsklick-Menü im Kalender selbst zusammen (ein-/ausschalten + Reihenfolge).
// Fest (immer aktiv): Kundenprofil, Verschieben, Check-in.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GripVertical, ChevronUp, ChevronDown, Lock, Save, MousePointerClick } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarMenuSettingsPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/calendar-menu`);
        setEntries(res.data?.entries || []);
      } catch {
        toast.error('Menü-Einstellungen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const move = (from, to) => {
    if (to < 0 || to >= entries.length) return;
    setEntries((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const toggle = (key) => {
    setEntries((prev) => prev.map((e) => (e.key === key && !e.fixed ? { ...e, enabled: !e.enabled } : e)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/calendar-menu`, {
        entries: entries.map((e) => ({ key: e.key, enabled: e.enabled })),
      });
      toast.success('Termin-Menü gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-400" data-testid="calendar-menu-loading">Lädt …</div>;
  }

  return (
    <div className="max-w-2xl" data-testid="calendar-menu-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MousePointerClick className="h-5 w-5 text-teal-600" />
            Termin-Menü (Rechtsklick im Kalender)
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Stelle zusammen, welche Aktionen beim Rechtsklick auf einen Termin erscheinen und in
            welcher Reihenfolge. Ziehe zum Sortieren oder nutze die Pfeile.
            <span className="font-medium text-gray-700"> Kundenprofil, Verschieben und Check-in sind immer aktiv.</span>
          </p>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden">
            {entries.map((e, i) => (
              <li
                key={e.key}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(ev) => ev.preventDefault()}
                onDrop={() => { if (dragIndex !== null) move(dragIndex, i); setDragIndex(null); }}
                className={`flex items-center gap-3 px-3 py-2.5 bg-white ${dragIndex === i ? 'opacity-50' : ''}`}
                data-testid={`calendar-menu-item-${e.key}`}
              >
                <GripVertical size={16} className="text-gray-300 cursor-grab flex-shrink-0" />
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                    data-testid={`calendar-menu-up-${e.key}`}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    disabled={i === entries.length - 1}
                    onClick={() => move(i, i + 1)}
                    data-testid={`calendar-menu-down-${e.key}`}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
                <span className="flex-1 text-sm font-medium text-gray-800">{e.label}</span>
                {e.fixed ? (
                  <span className="flex items-center gap-1 text-xs text-gray-400" data-testid={`calendar-menu-fixed-${e.key}`}>
                    <Lock size={12} /> Immer aktiv
                  </span>
                ) : (
                  <Switch
                    checked={e.enabled}
                    onCheckedChange={() => toggle(e.key)}
                    data-testid={`calendar-menu-toggle-${e.key}`}
                  />
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="calendar-menu-save">
              <Save size={16} className="mr-2" />
              {saving ? 'Speichert …' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarMenuSettingsPage;
